import React from "react";

import type { Record } from "~/tools/record";

import type { BorgContentPart } from "../models/borg-types";

import { ActionGroup } from "./_action-group";
import { ErrorPart } from "./_error-part";
import { isBlankText } from "./_message-thread-utils";
import type { PartGroup } from "./_message-utils";
import { SourceLinkGroup } from "./_source-link-group";
import { SuggestionGroup } from "./_suggestion-group";
import { TextPart } from "./_text-part";
import { ThinkingToggle } from "./_thinking-toggle";
import { ToolCallGroup } from "./_tool-call-group";
import type { ComponentViewMode } from "./_use-navigate-to-component";

export interface PartGroupRendererProps {
	group: PartGroup;
	isLiveThinking?: boolean;
	thinkingToggleKey: string;
	thinkingOpenByKey: { [key: string]: boolean };
	onThinkingOpenChange: (key: string, open: boolean) => void;
	onSuggestionClick?: (text: string) => void;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}

/** Narrows group.items to the specific part type. Safe because groupAdjacentParts groups by type. */
const narrowParts = <T extends BorgContentPart["type"]>(parts: ReadonlyArray<BorgContentPart>) =>
	parts as ReadonlyArray<Extract<BorgContentPart, { type: T }>>;

type GroupRenderer = (props: PartGroupRendererProps) => React.ReactNode;

const GROUP_RENDERERS: Record<PartGroup["key"], GroupRenderer> = {
	text: ({ group }) => {
		const parts = narrowParts<"text">(group.items);
		const hasVisibleContent = parts.some((part) => !isBlankText(part.content));
		if (!hasVisibleContent) {
			return null;
		}
		return <TextPart parts={parts} />;
	},
	thinking: ({ group, isLiveThinking, onThinkingOpenChange, thinkingOpenByKey, thinkingToggleKey }) => {
		const [part] = narrowParts<"thinking">(group.items);
		if (part == null || !part.content) {
			return null;
		}
		return (
			<ThinkingToggle
				isLive={isLiveThinking}
				onOpenChange={(open) => onThinkingOpenChange(thinkingToggleKey, open)}
				open={thinkingOpenByKey[thinkingToggleKey] ?? false}
				thinking={part.content}
			/>
		);
	},
	tool_call: ({ group }) => <ToolCallGroup parts={narrowParts<"tool_call">(group.items)} />,
	progress: () => null,
	suggestion: ({ group, onSuggestionClick }) => (
		<SuggestionGroup onSuggestionClick={onSuggestionClick} parts={narrowParts<"suggestion">(group.items)} />
	),
	source_link: ({ group, onNavigateToComponent }) => (
		<SourceLinkGroup onNavigateToComponent={onNavigateToComponent} parts={narrowParts<"source_link">(group.items)} />
	),
	action: ({ group, onNavigateToComponent }) => (
		<ActionGroup onNavigateToComponent={onNavigateToComponent} parts={narrowParts<"action">(group.items)} />
	),
	error: ({ group }) => {
		const [part] = narrowParts<"error">(group.items);
		if (part == null) {
			return null;
		}
		return <ErrorPart part={part} />;
	},
};

export const PartGroupRenderer: React.FC<PartGroupRendererProps> = (props) => GROUP_RENDERERS[props.group.key](props);
